import axios from "./axiosInstance";
import { 
  Absence, 
  AbsenceFilters, 
  AbsenceResponse, 
  AbsenceStats, 
  AbsenceStatsResponse, 
  StudentAlert, 
  StudentAlertResponse, 
  StudentAbsenceSummary, 
  StudentAbsenceListResponse, 
  StudentAbsenceDetails, 
  StudentAbsenceDetailsResponse 
} from "../types/absenceTypes";
import { Schedule } from "../types/planningTypes";

// Fetch the daily schedule for a teacher
export const getDailySchedule = async (
    teacherId: string
  ): Promise<{ data: Schedule[]; code: string; statusCode: number }> => {
    const response = await axios.get("/absences/daily-schedule", {
      params: { teacherId },
    });
  
    // Return the full response object
    return response.data;
  };

// Record attendance for a session
export const recordAttendance = async (
  planningId: string,
  absences: Absence[]
): Promise<number> => {
  const response = await axios.post<number>("/absences/attendance", {
    planningId,
    absences,
  });
  return response.data;
};

// Fetch absences based on filters
export const getAbsences = async (
  filters: AbsenceFilters
): Promise<AbsenceResponse> => {
  const response = await axios.get<AbsenceResponse>("/absences", { params: filters });
  return response.data;
};

// Update the status of an absence
export const updateAbsence = async (
  absenceId: string,
  updateFields: Partial<Absence>
): Promise<void> => {
  const response = await axios.patch(`/absences/${absenceId}`, updateFields);
  return response.data;
};

// Soft delete an absence
export const deleteAbsence = async (absenceId: string): Promise<void> => {
  const response = await axios.delete(`/absences/${absenceId}`);
  return response.data;
};

// Fetch absences for a specific student
export const getStudentAbsences = async (
  studentId: string,
  filters?: AbsenceFilters
): Promise<AbsenceResponse> => {
  const response = await axios.get<AbsenceResponse>(
    `/absences/student/${studentId}`,
    { params: filters }
  );
  return response.data;
};

// Fetch absence overview statistics
export const getAbsenceStats = async (): Promise<AbsenceStatsResponse> => {
  const response = await axios.get<AbsenceStatsResponse>('/absences/stats/overview');
  return response.data;
};

// Fetch students with absence alerts
export const getAbsenceAlerts = async (limit?: number): Promise<StudentAlertResponse> => {
  const response = await axios.get<StudentAlertResponse>('/absences/alerts', {
    params: { limit }
  });
  return response.data;
};

// Fetch paginated student absence summaries
export const getStudentAbsenceSummaries = async (
  page: number = 1,
  limit: number = 10,
  sortBy: 'totalAbsenceHours' | 'percentageOfThreshold' | 'name' | 'class' = 'totalAbsenceHours'
): Promise<StudentAbsenceListResponse> => {
  try {
    // Make a direct request to the backend with the sorting parameter
    const response = await axios.get<StudentAbsenceListResponse>('/absences/per-student', {
      params: { page, limit, sortBy }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error fetching student absence summaries:', error);
    // Return empty response with proper pagination structure
    return {
      data: [],
      pagination: {
        total: 0,
        page,
        limit,
        totalPages: 0
      }
    };
  }
};

// Fetch detailed absence history for a student
export const getStudentAbsenceDetails = async (
  studentId: string
): Promise<StudentAbsenceDetailsResponse> => {
  const response = await axios.get<StudentAbsenceDetailsResponse>(
    `/absences/student-details/${studentId}`
  );
  return response.data;
};

// Upload proof document for an absence
export const uploadAbsenceProof = async (
  absenceId: string,
  file: File
): Promise<any> => {
  // Create form data
  const formData = new FormData();
  formData.append('file', file);
  
  // Set headers for multipart/form-data
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  };
  
  const response = await axios.post(
    `/absences/${absenceId}/proof`,
    formData,
    config
  );
  
  return response.data;
};
